import React from 'react';
import {
  Image,
  ImageStyle,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import { colors } from '../../theme';
import LogoMark from '../LogoMark';
import Text from '../ui/Text';

/**
 * Whoever is signed in, as a circle.
 *
 * Three states in one place, so every screen showing a person shows the same
 * thing:
 *
 *   1. a photograph, if one has been chosen
 *   2. the first letter of their name, if there is a name
 *   3. the petal mark, when there is neither
 *
 * The initial rather than a grey silhouette. A silhouette says "missing image";
 * a letter says "this is you, and you have not added a picture" - and in a list
 * it still distinguishes one person from another, which is the whole job.
 *
 * The mark is the last resort rather than the first: it is the brand, not the
 * user, so it stands in only when nothing about the user is known yet.
 */

type Props = {
  /** A local `file://` or `content://` URI. */
  photo?: string | null;
  name?: string | null;
  /** Diameter in points. */
  size?: number;
  /*
   * Typed for both branches. The photo is an `Image` and the fallback a `View`,
   * and `ViewStyle` alone is not assignable to `ImageStyle` - the two disagree
   * about `overflow: 'scroll'`, which an Image cannot do.
   */
  style?: StyleProp<ViewStyle & ImageStyle>;
};

/**
 * The first letter, uppercased.
 *
 * Trimmed first, because " ravi" would otherwise letter as a space. Returns
 * null for anything with no letter in it at all, which sends the avatar to the
 * mark rather than drawing an empty circle.
 */
export const initialOf = (name?: string | null): string | null => {
  const trimmed = name?.trim();
  if (!trimmed) {
    return null;
  }
  const first = trimmed.charAt(0);
  return first ? first.toUpperCase() : null;
};

function ProfileAvatar({ photo, name, size = 62, style }: Props) {
  const initial = initialOf(name);
  const round = { width: size, height: size, borderRadius: size / 2 };

  if (photo) {
    return (
      <Image
        source={{ uri: photo }}
        style={[styles.photo, round, style]}
        // The picker returns whatever aspect the camera or the library had, so
        // the circle has to crop rather than letterbox.
        resizeMode="cover"
        accessible
        accessibilityRole="image"
        accessibilityLabel={name ? `${name}'s photo` : 'Profile photo'}
      />
    );
  }

  return (
    <View
      style={[styles.mark, round, style]}
      accessible
      accessibilityRole="image"
      accessibilityLabel={name ? `${name}, no photo` : 'No photo'}
    >
      {initial ? (
        /* Sized off the circle rather than a variant, so one component covers
           the 62pt account header and the 34pt row without a second style. */
        <Text
          color={colors.onPrimary}
          style={[styles.initial, { fontSize: size * 0.42, lineHeight: size * 0.5 }]}
        >
          {initial}
        </Text>
      ) : (
        <LogoMark size={size * 0.48} color={colors.onPrimary} />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  photo: {
    backgroundColor: colors.surfaceAccent,
  },
  mark: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary,
  },
  initial: {
    fontWeight: '700',
    // Otherwise Android centres the glyph against the font's full metrics and
    // the letter sits a couple of points high in the circle.
    textAlign: 'center',
    includeFontPadding: false,
  },
});

export default ProfileAvatar;
